/**
 * One wording, one emptiness test, for every "you picked something that has
 * nothing behind it" case in Supporting Tools. Screens differ wildly in how they
 * load dependent data, so without a shared definition each one invented its own
 * idea of "empty" — and several simply showed a blank form with no explanation.
 */

export const NO_DATA_MESSAGE = 'No data available on this record';

/** Toast heading that goes with NO_DATA_MESSAGE. */
export const NO_DATA_SUMMARY = 'No Data';

/**
 * True when an API result carries nothing worth showing.
 *
 * The object case is the one that matters: several EasyChit endpoints answer a
 * miss with HTTP 200 and a fully shaped DTO whose every field is the C# default
 * (0 / null), not with 404 or an empty body — `GetBusinessRefByTicket` on a
 * ticket with no active reference, for instance. A plain `!res` test passes
 * those straight through and the screen renders a form full of blanks.
 */
export function isEmptyResult(res) {
  if (res === null || res === undefined) return true;
  if (Array.isArray(res)) return res.length === 0;
  if (typeof res === 'string') return res.trim() === '';
  if (typeof res === 'object') {
    const values = Object.values(res);
    if (values.length === 0) return true;
    // Every field at its default => the row was never found. A record that
    // genuinely exists always carries at least one id, name or date.
    return values.every((v) => v === null || v === undefined || v === '' || v === 0);
  }
  return false;
}

/**
 * Colour for an inline load message. "Nothing found" / "not configured" outcomes
 * are conditions, not failures, so they render amber; only genuine load failures
 * ("Could not load ...") stay red.
 */
export const WARN_TEXT_COLOR = '#B45309';
export const ERROR_TEXT_COLOR = '#DC2626';

export function isNoDataNotice(msg) {
  if (!msg) return false;
  return msg === NO_DATA_MESSAGE || /^no\b/i.test(msg) || /not found|not configured/i.test(msg);
}

export function inlineNoticeColor(msg) {
  return isNoDataNotice(msg) ? WARN_TEXT_COLOR : ERROR_TEXT_COLOR;
}
